import { useState } from "react";
import { Loader2, ShieldAlert, AlertTriangle, Wallet, Clock, RefreshCw, Copy, Check, MessageCircle, Info, Pencil, XCircle } from "lucide-react";
import { toast } from "sonner";
import { fmtMoney, errorMessage } from "@/api";

export default function ResumenStep({ boot, moto, cobertura, titular, loading, onBack, onEmitir, onGuardar, onCancelar, onConsultar, onCambiarCobertura, onEditarDatos, recuperado, pagoHabilitado }) {
  const esRobo = cobertura?.tipo === "robo";
  const CONSENT_FALLBACK = {
    checkbox_texto: "Comprendo que hasta realizar la inspección de mi moto en la app de ATM Seguros y que ATM la apruebe, la cobertura de robo NO está activa: mi póliza cubre únicamente Responsabilidad Civil, aunque haya pagado la cobertura de robo.",
    importante_texto: "Luego de pagar deberás instalar la app de ATM Seguros y realizar la inspección enviando las fotos de tu moto. Hasta que ATM apruebe la inspección, tu cobertura funciona SOLO como Responsabilidad Civil, aunque hayas pagado la cobertura de robo. La cobertura de robo se activa recién cuando ATM aprueba la inspección en su app.",
  };
  const consent = { ...CONSENT_FALLBACK, ...(boot?.consentimiento || {}) };
  const [acepta, setAcepta] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [guardado, setGuardado] = useState(null);
  const [copiado, setCopiado] = useState(false);
  const [verDetalle, setVerDetalle] = useState(false);
  const patente = titular?.patente === "A/D" ? "A/D (a declarar)" : titular?.patente;
  const detalle = boot?.coberturas_config?.[cobertura?.codigo]?.detalle || "";
  const pago = boot?.pago || { abierto: true };
  const pagoAbierto = pago.abierto !== false;
  const requiereValidacion = esRobo ? boot?.validacion?.robo !== false : boot?.validacion?.rc !== false;
  const emisionManual = boot?.validacion?.manual === true;
  const [confirmar, setConfirmar] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [cancelado, setCancelado] = useState(null);
  const [consultando, setConsultando] = useState(false);

  const hablarWhatsApp = async () => {
    setConsultando(true);
    try {
      const data = await onConsultar();
      if (data?.whatsapp_url) window.open(data.whatsapp_url, "_blank", "noopener");
    } catch (err) {
      toast.error(errorMessage(err, "No pudimos preparar el WhatsApp. Intentá de nuevo."));
    } finally {
      setConsultando(false);
    }
  };

  const cancelarEmision = async () => {
    setCancelando(true);
    try {
      const data = await onCancelar();
      setCancelado(data);
    } catch (err) {
      toast.error(errorMessage(err, "No pudimos cancelar la solicitud. Intentá de nuevo."));
    } finally {
      setCancelando(false);
    }
  };

  const guardarAhora = async (intento = "despues") => {
    setGuardando(true);
    try {
      const data = await onGuardar({ ...(esRobo ? { consentimiento_robo: true } : {}), intento });
      setGuardado({ ...data, intento });
    } catch (err) {
      toast.error(errorMessage(err, "No pudimos guardar tu cotización. Intentá de nuevo."));
    } finally {
      setGuardando(false);
    }
  };

  const copiarLink = () => {
    navigator.clipboard?.writeText(guardado.recovery_url);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  };

  if (guardado) {
    const esAhora = guardado.intento === "ahora";
    if (guardado.intento === "manual") {
      return (
        <div data-testid="step-solicitud-manual" className="pt-2">
          <div className="w-16 h-16 rounded-full bg-[#25D366]/15 flex items-center justify-center mx-auto mb-4">
            <MessageCircle className="w-8 h-8 text-[#25D366]" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2 text-center">¡Recibimos tu solicitud!</h1>
          <p className="text-slate-500 text-center text-sm mb-6">Ya tenemos todos tus datos cargados. Te enviamos el link de pago por WhatsApp y tu póliza se emite recién cuando se acredita el pago. <b>No se emite nada antes de que pagues.</b></p>
          {guardado.whatsapp_url && (
            <a data-testid="manual-whatsapp-button" href={guardado.whatsapp_url} target="_blank" rel="noopener noreferrer"
              className="w-full h-14 rounded-full bg-[#25D366] text-white font-heading font-semibold text-lg flex items-center justify-center gap-2 transition mb-3">
              <MessageCircle className="w-5 h-5" /> Solicitar link de pago
            </a>
          )}
          <a href="/" data-testid="manual-volver-inicio" className="block text-center text-sm text-slate-500 underline underline-offset-2 mt-2">Volver al inicio</a>
        </div>
      );
    }
    return (
      <div data-testid="step-guardado" className="pt-2">
        <div className="w-16 h-16 rounded-full bg-[#25D366]/15 flex items-center justify-center mx-auto mb-4">
          {esAhora ? <MessageCircle className="w-8 h-8 text-[#25D366]" /> : <Clock className="w-8 h-8 text-indigo-600" />}
        </div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2 text-center">{esAhora ? "Un último paso para pagar" : "¡Guardá tu cotización en WhatsApp!"}</h1>
        <p className="text-slate-500 text-center text-sm mb-6">{esAhora
          ? "Para continuar necesitás enviarnos un WhatsApp. Es rápido y no tenés que escribir nada."
          : "Llevate tu cotización a tu WhatsApp para no perderla: la retomás y pagás cuando quieras, con todos tus datos ya cargados."}</p>

        {esAhora ? (
          <>
            <ol className="bg-white border border-slate-200 rounded-[20px] p-4 mb-4 space-y-3" data-testid="wa-pasos">
              {[
                "Tocá el botón verde: se abre WhatsApp con un mensaje ya escrito.",
                "Enviános ese mensaje (ya viene listo, no escribas nada).",
                "En ese mismo chat, tocá el enlace que enviaste y volvés acá para pagar.",
              ].map((t, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <span className="w-6 h-6 rounded-full bg-emerald-600 text-white font-heading font-bold text-xs flex items-center justify-center shrink-0">{i + 1}</span>
                  <span className="text-sm text-slate-700">{t}</span>
                </li>
              ))}
            </ol>
            <a data-testid="guardado-whatsapp-button" href={guardado.whatsapp_url} target="_blank" rel="noopener noreferrer"
              className="w-full h-14 rounded-full bg-[#25D366] text-white font-heading font-semibold text-lg flex items-center justify-center gap-2 transition mb-3">
              <MessageCircle className="w-5 h-5" /> Enviar WhatsApp
            </a>
          </>
        ) : (
          <>
            <div className="bg-white border border-slate-200 rounded-[20px] p-4 mb-4">
              <p className="text-xs text-slate-500 mb-2">Tu link para retomar el pago:</p>
              <div className="flex items-center gap-2">
                <input readOnly data-testid="recovery-link-input" value={guardado.recovery_url}
                  className="flex-1 min-w-0 text-xs font-data bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-slate-700" />
                <button data-testid="copiar-recovery-link" onClick={copiarLink}
                  className="shrink-0 h-9 w-9 rounded-lg border border-slate-300 flex items-center justify-center text-slate-600">
                  {copiado ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {guardado.whatsapp_url && (
              <>
                <div className="bg-emerald-50 border border-emerald-200 rounded-[20px] p-4 mb-4 text-sm text-emerald-900">
                  Envianos un WhatsApp con tu link y queda agendado para que lo pagues más tarde.
                </div>
                <a data-testid="guardado-whatsapp-button" href={guardado.whatsapp_url} target="_blank" rel="noopener noreferrer"
                  className="w-full h-14 rounded-full bg-[#25D366] text-white font-heading font-semibold text-lg flex items-center justify-center gap-2 transition mb-3">
                  <MessageCircle className="w-5 h-5" /> Guardar en WhatsApp
                </a>
              </>
            )}
            <a data-testid="guardado-pagar-ahora" href={guardado.recovery_url}
              className="block text-center text-sm text-emerald-700 underline underline-offset-2">Prefiero pagar ahora</a>
          </>
        )}
      </div>
    );
  }

  if (cancelado) {
    return (
      <div data-testid="step-cancelado" className="pt-2">
        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
          <XCircle className="w-8 h-8 text-red-500" />
        </div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2 text-center">Listo, cancelamos tu solicitud</h1>
        <p className="text-slate-500 text-center text-sm mb-6">No se emitió ninguna póliza ni se generó ningún pago. Tu cotización quedó guardada por si cambiás de idea.</p>
        {cancelado.recovery_url && (
          <div className="bg-white border border-slate-200 rounded-[20px] p-4 mb-4" data-testid="cancelado-recovery-box">
            <p className="text-xs text-slate-500 mb-1.5">Recuperala con un click cuando quieras:</p>
            <a data-testid="cancelado-recovery-link" href={cancelado.recovery_url} className="text-sm font-semibold text-emerald-700 underline underline-offset-2 break-all">{cancelado.recovery_url}</a>
          </div>
        )}
        {cancelado.whatsapp_url && (
          <a data-testid="cancelado-whatsapp-button" href={cancelado.whatsapp_url} target="_blank" rel="noopener noreferrer"
            className="w-full h-14 rounded-full bg-[#25D366] text-white font-heading font-semibold text-lg flex items-center justify-center gap-2 transition mb-3">
            <MessageCircle className="w-5 h-5" /> Avisar la cancelación por WhatsApp
          </a>
        )}
        <a href="/" data-testid="cancelado-volver-inicio" className="block text-center text-sm text-emerald-700 underline underline-offset-2 mt-2">Cotizar de nuevo</a>
      </div>
    );
  }

  if (confirmar) {
    return (
      <div data-testid="step-confirmar-emision" className="pt-2">
        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8 text-emerald-600" />
        </div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2 text-center">¡Tu póliza ya está lista!</h1>
        <p className="text-slate-500 text-center text-sm mb-5">Tu solicitud está confirmada y lista para pagar.</p>

        <div className="bg-white border border-slate-100 rounded-[24px] p-5 mb-4 shadow-[0_12px_36px_rgba(0,0,0,0.06)]" data-testid="confirmar-detalle">
          <div className="flex justify-between gap-3 text-sm mb-1.5"><span className="text-slate-500">Cobertura</span><span className="font-medium text-slate-800 text-right">{cobertura?.nombre_visible || cobertura?.descripcion}</span></div>
          <div className="flex justify-between gap-3 text-sm mb-1.5"><span className="text-slate-500">Moto</span><span className="font-medium text-slate-800 text-right">{moto?.marca_desc} {moto?.modelo_desc} {moto?.anio}</span></div>
          <div className="flex justify-between gap-3 text-sm"><span className="text-slate-500">Titular</span><span className="font-medium text-slate-800 text-right">{titular?.apellido}, {titular?.nombre}</span></div>
          <div className="border-t border-slate-100 mt-3 pt-3 flex items-end justify-between">
            <span className="text-sm text-slate-500">Importe</span>
            <p className="font-heading text-2xl font-bold text-slate-900" data-testid="confirmar-importe">{fmtMoney(cobertura?.impcuotas)}<span className="text-sm font-normal text-slate-500">/mes</span></p>
          </div>
        </div>

        <div className="bg-amber-50 border-2 border-amber-200 rounded-[20px] p-4 mb-6 flex gap-3" data-testid="confirmar-aviso-emision">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-900"><b>Tené en cuenta:</b> al tocar "Pagar ahora" se <b>emite tu póliza a tu nombre</b> y te llevamos a MercadoPago para abonarla.</p>
        </div>

        <button data-testid="confirmar-pagar-mp" disabled={loading} onClick={() => onEmitir(esRobo ? { consentimiento_robo: true } : {})}
          className="w-full h-14 rounded-[14px] bg-[#009EE3] hover:bg-[#0085c2] disabled:bg-slate-300 text-white font-heading font-semibold text-lg transition flex items-center justify-center gap-2 mb-3">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <img src="/assets/mp-logo.png" alt="" className="h-6 w-auto" />}
          {loading ? "Emitiendo y direccionando..." : "Pagar ahora con Mercado Pago"}
        </button>
        <button data-testid="confirmar-cancelar" disabled={loading || cancelando || consultando} onClick={cancelarEmision}
          className="w-full h-12 rounded-[14px] bg-white border-2 border-red-500 text-red-600 hover:bg-red-50 disabled:opacity-50 font-heading font-semibold text-sm transition flex items-center justify-center gap-2 mb-3">
          {cancelando ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
          Cancelar la emisión · No voy a contratar ahora
        </button>
        <button data-testid="confirmar-consulta-wa" disabled={loading || cancelando || consultando} onClick={hablarWhatsApp}
          className="w-full h-12 rounded-[14px] bg-white border-2 border-[#25D366] text-[#128C4A] hover:bg-[#25D366]/10 disabled:opacity-50 font-heading font-semibold text-sm transition flex items-center justify-center gap-2 mb-4">
          {consultando ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
          Hablar por WhatsApp · Tengo una consulta
        </button>
        <button type="button" data-testid="confirmar-volver" onClick={() => setConfirmar(false)} disabled={loading || cancelando || consultando}
          className="block mx-auto text-sm text-slate-500 hover:text-emerald-700 underline underline-offset-2">Volver al resumen</button>
      </div>
    );
  }

  return (
    <div data-testid="step-resumen" className="pt-2">
      <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">
        {esRobo ? "Estás contratando un seguro contra robo para tu moto" : "Revisá y confirmá tu cobertura"}
      </h1>
      <p className="text-slate-500 mb-6">Verificá que los datos sean correctos antes de continuar.</p>

      <div className="bg-white border border-slate-100 rounded-[24px] p-5 mb-3 shadow-[0_12px_36px_rgba(0,0,0,0.06)]" data-testid="resumen-detalle">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-emerald-600 shrink-0" />
            <p className="font-heading font-bold text-slate-900 leading-tight" data-testid="resumen-cobertura">{cobertura?.nombre_visible || cobertura?.descripcion}</p>
          </div>
          <img src="/api/uploads/atm-logo.png" alt="ATM Seguros" className="h-8 w-auto shrink-0" data-testid="resumen-atm-logo" />
        </div>

        {detalle && (
          <div className="mb-3">
            <button type="button" data-testid="resumen-ver-detalle" onClick={() => setVerDetalle((v) => !v)}
              className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 underline underline-offset-2">
              <Info className="w-3.5 h-3.5" /> {verDetalle ? "Ocultar qué incluye" : "Qué incluye esta cobertura"}
            </button>
            {verDetalle && (
              <p data-testid="resumen-detalle-texto" className="text-sm text-slate-600 whitespace-pre-line leading-relaxed mt-2 bg-slate-50 rounded-xl p-3 border border-slate-100">{detalle}</p>
            )}
          </div>
        )}

        <div className="border-t border-slate-100 pt-3 space-y-1.5 text-sm">
          <div className="flex justify-between"><span className="text-slate-500">Moto</span><span className="font-medium text-slate-800 text-right" data-testid="resumen-moto">{moto?.marca_desc} {moto?.modelo_desc} {moto?.anio}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Patente</span><span className="font-data text-slate-800" data-testid="resumen-patente">{patente}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">N° chasis</span><span className="font-data text-slate-800 text-right" data-testid="resumen-chasis">{titular?.chasis}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">N° motor</span><span className="font-data text-slate-800 text-right" data-testid="resumen-motor">{titular?.motor}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Titular</span><span className="font-medium text-slate-800 text-right">{titular?.apellido}, {titular?.nombre}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">DNI</span><span className="font-data text-slate-800" data-testid="resumen-dni">{titular?.nrodoc}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Celular</span><span className="font-medium text-slate-800 text-right" data-testid="resumen-celular">{titular?.tel_cel}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Email</span><span className="font-medium text-slate-800 text-right break-all" data-testid="resumen-email">{titular?.mail}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Domicilio</span><span className="font-medium text-slate-800 text-right" data-testid="resumen-domicilio">{titular?.calle} {titular?.altura}{titular?.piso ? ` Piso ${titular.piso}` : ""}{titular?.depto ? ` ${titular.depto}` : ""}</span></div>
        </div>
        <div className="border-t border-slate-100 mt-3 pt-3 flex items-end justify-between">
          <span className="text-sm text-slate-500">Importe</span>
          <div className="flex items-center gap-2">
            <p className="font-heading text-3xl font-bold text-slate-900" data-testid="resumen-importe">{fmtMoney(cobertura?.impcuotas)}<span className="text-base font-normal text-slate-500">/mes</span></p>
            <span className="inline-flex items-center rounded-md bg-[#009EE3] px-2 py-1.5" title="Pagás con Mercado Pago">
              <img src="/assets/mp-logo.png" alt="Mercado Pago" className="h-5 w-auto" data-testid="resumen-mp-logo" />
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center gap-6 mb-5">
        <button type="button" data-testid="resumen-cambiar-cobertura" onClick={onCambiarCobertura}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-700">
          <RefreshCw className="w-4 h-4" /> Cambiar cobertura
        </button>
        {onEditarDatos && (
          <button type="button" data-testid="resumen-editar-datos" onClick={onEditarDatos}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-700">
            <Pencil className="w-4 h-4" /> Corregir mis datos
          </button>
        )}
      </div>

      {esRobo && (
        <>
          <div className="bg-orange-50 border-2 border-orange-200 rounded-[20px] p-4 mb-4 flex gap-3" data-testid="resumen-importante">
            <AlertTriangle className="w-5 h-5 text-orange-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-heading font-bold text-orange-800 text-sm mb-1">¡IMPORTANTE!</p>
              <p className="text-sm text-orange-800">{consent.importante_texto}</p>
            </div>
          </div>
          <label className="flex items-start gap-3 bg-white border border-slate-200 rounded-[20px] p-4 mb-6 cursor-pointer" data-testid="resumen-consent-label">
            <input type="checkbox" data-testid="resumen-consent-checkbox" className="mt-1 w-5 h-5 accent-emerald-600" checked={acepta} onChange={(e) => setAcepta(e.target.checked)} />
            <span className="text-sm text-slate-700">{consent.checkbox_texto}</span>
          </label>
        </>
      )}

      {!pagoAbierto && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-[20px] p-4 mb-4 flex gap-3" data-testid="pago-cerrado-banner">
          <Clock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-heading font-bold text-amber-800 text-sm mb-1">Horario de pago</p>
            <p className="text-sm text-amber-800">{pago.mensaje || "Por seguridad, el pago está disponible solo en ciertos horarios."}{pago.horarios_texto ? ` Horario habilitado: ${pago.horarios_texto}.` : ""} Guardá tu cotización y retomá el pago en ese horario.</p>
          </div>
        </div>
      )}

      <div className="fixed bottom-0 left-0 right-0 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:p-6 bg-white/85 backdrop-blur-xl border-t border-slate-200 sm:border-t-0 sm:bg-transparent sm:backdrop-blur-none z-50">
        <div className="max-w-xl mx-auto flex flex-col gap-2">
          <div className="flex gap-3">
            <button data-testid="wizard-back-button" onClick={onBack} disabled={loading || guardando} className="h-14 px-6 rounded-[14px] border border-slate-300 text-slate-700 font-medium transition">Atras</button>
            {pagoAbierto ? (
              <button data-testid="resumen-pagar-button" disabled={loading || guardando || (esRobo && !acepta)}
                onClick={() => {
                  if (emisionManual && !pagoHabilitado) return guardarAhora("manual");
                  if (!recuperado && requiereValidacion) return guardarAhora("ahora");
                  setConfirmar(true);
                }}
                className="flex-1 h-14 rounded-[14px] bg-[#1C1C1E] hover:bg-zinc-800 disabled:bg-slate-300 text-white font-heading font-semibold text-lg transition flex items-center justify-center gap-2">
                {loading || guardando ? <Loader2 className="w-5 h-5 animate-spin" /> : <Wallet className="w-5 h-5" />}
                <span className="leading-tight text-center">{loading ? "Direccionando a MercadoPago..." : guardando ? (emisionManual ? "Enviando..." : "Preparando tu link...") : emisionManual ? "Quiero contratar" : "Pagar ahora"}</span>
              </button>
            ) : (
              <button data-testid="resumen-guardar-horario-button" disabled={loading || guardando || recuperado || (esRobo && !acepta)}
                onClick={() => guardarAhora("despues")}
                className="flex-1 h-14 rounded-[14px] bg-[#1C1C1E] hover:bg-zinc-800 disabled:bg-slate-300 text-white font-heading font-semibold text-lg transition flex items-center justify-center gap-2">
                {guardando ? <Loader2 className="w-5 h-5 animate-spin" /> : <Clock className="w-5 h-5" />}
                <span className="leading-tight text-center">{recuperado ? "Pago disponible en el horario" : guardando ? "Guardando..." : "Guardar mi cotización"}</span>
              </button>
            )}
          </div>
          {pagoAbierto && (
            <button data-testid="resumen-pagar-luego-button" disabled={loading || guardando || (esRobo && !acepta)}
              onClick={() => guardarAhora("despues")}
              className="h-12 rounded-[14px] bg-white border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 font-heading font-semibold text-sm transition flex items-center justify-center gap-2">
              {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />}
              {guardando ? "Guardando..." : "Prefiero pagar más tarde"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
